import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import Sidebar from "./Sidebar";
import {
  getCurrentUser,
  educationAPI,
  locationsAPI,
  getDashboardStats,
} from "../api/axiosClient";
import { UserMeResponse, UserStatus } from "../api/types";
import StudentCreateForm from "./StudentCreateForm";
import StudentEditForm from "./StudentEditForm";
import SystemAdminCreateForm from "./SystemAdminCreateForm";
import {
  SchoolsView,
  EventView,
  EventDetailView,
  AccountView,
  ReceivablesView,
  ReceivableDetailView,
  CreateReceivableView,
  // ReportsView,
  SettingsView,
} from "../views";
import BrandCreateForm from "./BrandCreateForm";
import BrandEditForm from "./BrandEditForm";
import ClassroomCreateForm from "./ClassroomCreateForm";
import ClassroomEditForm from "./ClassroomEditForm";
import SchoolManagerCreateForm from "./SchoolManagerCreateForm";
import SchoolUserCreateForm from "./SchoolUserCreateForm";
import SchoolCreateForm from "./SchoolCreateForm";
import {
  Users,
  GraduationCap,
  Building2,
  Activity,
  Calendar,
  BookOpen,
  Award,
  Plus,
  Edit,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Facebook,
  Globe,
  CheckCircle,
  Eye,
  User,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  SchoolBrand,
  SchoolManager,
  SystemAdmin,
  SystemAdminsResponse,
  Parent,
  ParentsResponse,
  ParentDto,
  City,
  District,
  Neighborhood,
  CitiesResponse,
  DistrictsResponse,
  NeighborhoodsResponse,
} from "../api/types";
import { translateRole } from "../lib/utils";
import { toast } from "react-hot-toast";
import SchoolManagerEditForm from "./SchoolManagerEditForm";
import SchoolUserEditForm from "./SchoolUserEditForm";
import SystemAdminEditForm from "./SystemAdminEditForm";
import ParentCreateForm from "./ParentCreateForm";
import ParentEditForm from "./ParentEditForm";
import ParentAssignmentForm from "./ParentAssignmentForm";
import ParentDetailModal from "./ParentDetailModal";
import StudentDetailModal from "./StudentDetailModal";
import HelpSupportPage from "./HelpAndSupport";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams<{ id: string }>();
  const { logout } = useAuth();

 const getUserTypeText = (userType: number): string => {
  switch (userType) {
    case 2:
      return "Okul Yöneticisi";
    case 5:
      return "Okul Kullanıcısı";
    default:
      return "Bilinmeyen";
  }
};

const getUserTypeBadgeColor = (userType: number): string => {
  switch (userType) {
    case 2:
      return "bg-blue-100 text-blue-800";
    case 5:
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getUserTypeIcon = (
  userType: number,
  size: "small" | "large" = "small",
) => {
  const iconClass = size === "large" ? "w-5 h-5" : "w-3 h-3";
  switch (userType) {
    case 2:
      return <User className={iconClass} />;
    case 5:
      return <Users className={iconClass} />;
    default:
      return <User className={iconClass} />;
  }
};

const getUserTypeBadgeBgColor = (userType: number): string => {
  switch (userType) {
    case 2:
      return "bg-blue-500";
    case 5:
      return "bg-green-500";
    default:
      return "bg-gray-500";
  }
};

  // URL'den view'ı oku
  const getViewFromPath = (pathname: string): string => {
    const path = pathname.replace("/", "");
    if (path === "" || path === "dashboard") return "dashboard";
    // Alt route'ları da destekle (örn: events/abc123 -> events, school-payments/create -> school-payments)
    const basePath = path.split("/")[0];
    return basePath;
  };

  const [activeView, setActiveView] = useState<string>(
    getViewFromPath(location.pathname),
  );
  const [userDropdownOpen, setUserDropdownOpen] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<UserMeResponse["data"] | null>(
    null,
  );
  const [userLoading, setUserLoading] = useState<boolean>(true);

  // URL değiştiğinde view'ı güncelle
  useEffect(() => {
    const newView = getViewFromPath(location.pathname);
    setActiveView(newView);
  }, [location.pathname]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (userDropdownOpen && !target.closest(".user-dropdown")) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen]);

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      setLoadingDashboardStats(true);
      try {
        const response = await getDashboardStats();
        if (response.success && response.data) {
          setDashboardStats(response.data);
        }
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoadingDashboardStats(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // Fetch current user information
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setUserLoading(true);
        const response = await getCurrentUser();
        if (response.success && response.data) {
          setCurrentUser(response.data);
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
      } finally {
        setUserLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const [showBrandCreateForm, setShowBrandCreateForm] =
    useState<boolean>(false);
  const [showBrandEditForm, setShowBrandEditForm] = useState<boolean>(false);
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);

  // Brands state
  const [brands, setBrands] = useState<SchoolBrand[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");

  // School delete state
  const [deletingSchoolId, setDeletingSchoolId] = useState<string | null>(null);
  const [deletingStudentId, setDeletingStudentId] = useState<string | null>(
    null,
  );
  const [deletingClassroomId, setDeletingClassroomId] = useState<string | null>(
    null,
  );
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [deleteSchoolLoading, setDeleteSchoolLoading] =
    useState<boolean>(false);
  const [deletedSchoolId, setDeletedSchoolId] = useState<string | null>(null);

  // Edit state
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successAction, setSuccessAction] = useState<
    | "create"
    | "update"
    | "delete"
    | "createSchool"
    | "updateSchool"
    | "deleteSchool"
    | "createStudent"
    | "updateStudent"
    | "deleteStudent"
    | "createUser"
    | "deleteUser"
    | "createSchoolUser"
    | "deleteSchoolUser"
    | "updateSchoolUser"
    | "updateSchoolManager"
    | "createSystemAdmin"
    | "updateSystemAdmin"
    | "deleteSystemAdmin"
    | "createClassroom"
    | "updateClassroom"
    | "deleteClassroom"
    | "createParent"
    | "updateParent"
    | "deleteParent"
    | "createEvent"
    | "updateEvent"
    | "deleteEvent"
  >("create");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deletingBrandId, setDeletingBrandId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deletingParentId, setDeletingParentId] = useState<string | null>(null);

  // Students state
  const [students, setStudents] = useState<any[]>([]);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);
  const [studentsPagination, setStudentsPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [studentSearchTerm, setStudentSearchTerm] = useState<string>("");
  const [showStudentCreateForm, setShowStudentCreateForm] =
    useState<boolean>(false);
  const [showStudentEditForm, setShowStudentEditForm] =
    useState<boolean>(false);
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [schoolId, setSchoolId] = useState<string>("");

  // Event detail state - now handled via URL routing
  // const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Receivable detail state - now handled via URL routing
  // const [selectedReceivableId, setSelectedReceivableId] = useState<string | null>(null);
  // const [showCreateReceivable, setShowCreateReceivable] = useState<boolean>(false);

  // Student parents state
  const [showParentAssignmentForm, setShowParentAssignmentForm] =
    useState<boolean>(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  );
  const [selectedStudentName, setSelectedStudentName] = useState<string>("");

  // Parents state
  const [parents, setParents] = useState<Parent[]>([]);
  const [loadingParents, setLoadingParents] = useState<boolean>(false);
  const [parentsPagination, setParentsPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [parentSearchTerm, setParentSearchTerm] = useState<string>("");
  // const [showParentForm, setShowParentForm] = useState<boolean>(false);
  // const [isSubmittingParent, setIsSubmittingParent] = useState<boolean>(false);

  // Parent edit state
  // const [isEditingParent, setIsEditingParent] = useState<boolean>(false);
  // const [editingParentId, setEditingParentId] = useState<string | null>(null);

  // Parent form state
  // const [parentForm, setParentForm] = useState({
  //   firstName: '',
  //   lastName: '',
  //   email: '',
  //   phoneCode: '90',
  //   phoneNumber: '',
  //   relationship: 'Mother', // Mother, Father, Guardian
  //   isPrimary: false
  // });

  // Classrooms state
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState<boolean>(false);
  const [classroomsPagination, setClassroomsPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [classroomSearchTerm, setClassroomSearchTerm] = useState<string>("");
  const [showClassroomCreateForm, setShowClassroomCreateForm] =
    useState<boolean>(false);
  const [showClassroomEditForm, setShowClassroomEditForm] =
    useState<boolean>(false);
  const [editingClassroomId, setEditingClassroomId] = useState<string | null>(
    null,
  );

  // School Create Form state
  const [showSchoolCreateForm, setShowSchoolCreateForm] =
    useState<boolean>(false);
  const [schoolBrands, setSchoolBrands] = useState<SchoolBrand[]>([]);
  const [loadingSchoolBrands, setLoadingSchoolBrands] =
    useState<boolean>(false);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<SchoolBrand | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<{
    id: string;
    label: string;
    value: string;
  } | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<{
    id: string;
    label: string;
    value: string;
  } | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<{
    id: string;
    label: string;
    value: string;
  } | null>(null);

  // Users state (School Managers)
  const [users, setUsers] = useState<SchoolManager[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [usersPagination, setUsersPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [userSearchTerm, setUserSearchTerm] = useState<string>("");
  const [showUserForm, setShowUserForm] = useState<boolean>(false);

  // School Users state
  const [schoolUsers, setSchoolUsers] = useState<SchoolManager[]>([]);
  const [loadingSchoolUsers, setLoadingSchoolUsers] = useState<boolean>(false);
  const [schoolUsersPagination, setSchoolUsersPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [schoolUserSearchTerm, setSchoolUserSearchTerm] = useState<string>("");
  const [showSchoolUserForm, setShowSchoolUserForm] = useState<boolean>(false);

  // School Manager Edit Form state
  const [showSchoolManagerEditForm, setShowSchoolManagerEditForm] =
    useState<boolean>(false);
  const [editingSchoolManagerId, setEditingSchoolManagerId] = useState<
    string | null
  >(null);

  // School User Edit Form state
  const [showSchoolUserEditForm, setShowSchoolUserEditForm] =
    useState<boolean>(false);
  const [editingSchoolUserId, setEditingSchoolUserId] = useState<string | null>(
    null,
  );

  // Parent Edit Form state
  const [showParentCreateForm, setShowParentCreateForm] =
    useState<boolean>(false);
  const [showParentEditForm, setShowParentEditForm] = useState<boolean>(false);
  const [editingParentId, setEditingParentId] = useState<string | null>(null);

  // Parent Detail Modal state
  const [showParentDetailModal, setShowParentDetailModal] =
    useState<boolean>(false);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);

  // Student Detail Modal state
  const [showStudentDetailModal, setShowStudentDetailModal] =
    useState<boolean>(false);
  const [selectedStudentDetailId, setSelectedStudentDetailId] = useState<
    string | null
  >(null);

  // System Admins state
  const [systemAdmins, setSystemAdmins] = useState<SystemAdmin[]>([]);
  const [loadingSystemAdmins, setLoadingSystemAdmins] =
    useState<boolean>(false);
  const [systemAdminsPagination, setSystemAdminsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  });
  const [systemAdminSearchTerm, setSystemAdminSearchTerm] =
    useState<string>("");
  const [deletingSystemAdminId, setDeletingSystemAdminId] = useState<
    string | null
  >(null);
  const [showSystemAdminCreateForm, setShowSystemAdminCreateForm] =
    useState<boolean>(false);
  const [showSystemAdminEditForm, setShowSystemAdminEditForm] =
    useState<boolean>(false);
  const [editingSystemAdminId, setEditingSystemAdminId] = useState<
    string | null
  >(null);

  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [loadingDashboardStats, setLoadingDashboardStats] =
    useState<boolean>(false);

  const countryCodes = [
    { code: "90", name: "Türkiye (+90)", flag: "🇹🇷" },
    { code: "1", name: "USA (+1)", flag: "🇺🇸" },
    { code: "44", name: "UK (+44)", flag: "🇬🇧" },
    { code: "49", name: "Germany (+49)", flag: "🇩🇪" },
    { code: "33", name: "France (+33)", flag: "🇫🇷" },
    { code: "39", name: "Italy (+39)", flag: "🇮🇹" },
    { code: "34", name: "Spain (+34)", flag: "🇪🇸" },
    { code: "31", name: "Netherlands (+31)", flag: "🇳🇱" },
    { code: "32", name: "Belgium (+32)", flag: "🇧🇪" },
    { code: "46", name: "Sweden (+46)", flag: "🇸🇪" },
    { code: "47", name: "Norway (+47)", flag: "🇳🇴" },
    { code: "45", name: "Denmark (+45)", flag: "🇩🇰" },
    { code: "358", name: "Finland (+358)", flag: "🇫🇮" },
    { code: "7", name: "Russia (+7)", flag: "🇷🇺" },
    { code: "86", name: "China (+86)", flag: "🇨🇳" },
    { code: "81", name: "Japan (+81)", flag: "🇯🇵" },
    { code: "82", name: "South Korea (+82)", flag: "🇰🇷" },
    { code: "91", name: "India (+91)", flag: "🇮🇳" },
    { code: "971", name: "UAE (+971)", flag: "🇦🇪" },
    { code: "966", name: "Saudi Arabia (+966)", flag: "🇸🇦" },
    { code: "20", name: "Egypt (+20)", flag: "🇪🇬" },
    { code: "27", name: "South Africa (+27)", flag: "🇿🇦" },
    { code: "55", name: "Brazil (+55)", flag: "🇧🇷" },
    { code: "54", name: "Argentina (+54)", flag: "🇦🇷" },
    { code: "52", name: "Mexico (+52)", flag: "🇲🇽" },
    { code: "1-CA", name: "Canada (+1)", flag: "🇨🇦" },
    { code: "61", name: "Australia (+61)", flag: "🇦🇺" },
    { code: "64", name: "New Zealand (+64)", flag: "🇳🇿" },
  ];

  // Format phone number based on country code
  const formatPhoneNumber = (
    phoneCode: string,
    phoneNumber: string,
  ): string => {
    if (!phoneCode || !phoneNumber) return "";

    const cleanNumber = phoneNumber.replace(/\D/g, ""); // Remove non-digits

    switch (phoneCode) {
      case "90": // Türkiye: (5XX) XXX-XXXX
        if (cleanNumber.length === 10) {
          return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(
            3,
            6,
          )}-${cleanNumber.substring(6)}`;
        }
        break;
      case "1": // USA/Canada: (XXX) XXX-XXXX
        if (cleanNumber.length === 10) {
          return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(
            3,
            6,
          )}-${cleanNumber.substring(6)}`;
        }
        break;
      case "44": // UK: XXXX XXXXXX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 4)} ${cleanNumber.substring(4)}`;
        }
        break;
      case "49": // Germany: XXX XXXXXXXX
        if (cleanNumber.length === 11) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(3)}`;
        }
        break;
      case "33": // France: X XX XX XX XX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 1)} ${cleanNumber.substring(
            1,
            3,
          )} ${cleanNumber.substring(3, 5)} ${cleanNumber.substring(
            5,
            7,
          )} ${cleanNumber.substring(7)}`;
        }
        break;
      case "39": // Italy: XXX XXX XXXX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "34": // Spain: XXX XXX XXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "31": // Netherlands: X XXXXXXXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 1)} ${cleanNumber.substring(1)}`;
        }
        break;
      case "32": // Belgium: XXX XXX XXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "46": // Sweden: XXX XXX XXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "47": // Norway: XXX XX XXX
        if (cleanNumber.length === 8) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            5,
          )} ${cleanNumber.substring(5)}`;
        }
        break;
      case "45": // Denmark: XX XX XX XX
        if (cleanNumber.length === 8) {
          return `${cleanNumber.substring(0, 2)} ${cleanNumber.substring(
            2,
            4,
          )} ${cleanNumber.substring(4, 6)} ${cleanNumber.substring(6)}`;
        }
        break;
      case "358": // Finland: XXX XXX XXXX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "7": // Russia: (XXX) XXX-XX-XX
        if (cleanNumber.length === 10) {
          return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(
            3,
            6,
          )}-${cleanNumber.substring(6, 8)}-${cleanNumber.substring(8)}`;
        }
        break;
      case "86": // China: XXX XXXX XXXX
        if (cleanNumber.length === 11) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            7,
          )} ${cleanNumber.substring(7)}`;
        }
        break;
      case "81": // Japan: XX XXXX XXXX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 2)} ${cleanNumber.substring(
            2,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "82": // South Korea: XXX XXXX XXXX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            7,
          )} ${cleanNumber.substring(7)}`;
        }
        break;
      case "91": // India: XXXXX XXXXX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 5)} ${cleanNumber.substring(5)}`;
        }
        break;
      case "971": // UAE: XXX XXX XXXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "966": // Saudi Arabia: XXX XXX XXXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "20": // Egypt: XXX XXX XXXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "27": // South Africa: XXX XXX XXXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "55": // Brazil: (XX) XXXXX-XXXX
        if (cleanNumber.length === 10) {
          return `(${cleanNumber.substring(0, 2)}) ${cleanNumber.substring(
            2,
            7,
          )}-${cleanNumber.substring(7)}`;
        }
        break;
      case "54": // Argentina: XXX XXX-XXXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )}-${cleanNumber.substring(6)}`;
        }
        break;
      case "52": // Mexico: XXX XXX XXXX
        if (cleanNumber.length === 10) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "61": // Australia: XXX XXX XXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
      case "64": // New Zealand: XXX XXX XXXX
        if (cleanNumber.length === 9) {
          return `${cleanNumber.substring(0, 3)} ${cleanNumber.substring(
            3,
            6,
          )} ${cleanNumber.substring(6)}`;
        }
        break;
    }

    // Fallback: return original number if no specific format matches
    return phoneNumber;
  };

  // Initial data fetch - moved after fetchBrands definition

  // Fetch brands from API
  const fetchBrands = useCallback(async (page: number = 1, search?: string) => {
    console.log(
      "🔄 DEBUG: fetchBrands çağrıldı - page:",
      page,
      "search:",
      search,
      "activeView:",
      activeView,
    );

    setLoading(true);

    try {
      const response = await educationAPI.listSchoolBrands({
        Page: page,
        Size: 10,
        Q: search || "",
        OrderBy: 1, // Desc
        SortBy: "Name",
      });

      if (response.success && response.data) {
        setBrands(response.data.items || []);
        setPagination({
          pageNumber: response.data.pageNumber || 1,
          totalPages: response.data.totalPages || 1,
          totalCount: response.data.totalCount || 0,
          hasPreviousPage: response.data.hasPreviousPage || false,
          hasNextPage: response.data.hasNextPage || false,
        });
      } else {
        console.error("Failed to fetch brands:", response);
        toast.error("Markalar yüklenirken hata oluştu");
        setBrands([]);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Markalar yüklenirken hata oluştu");
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch school brands for school create form
  const fetchSchoolBrandsForForm = useCallback(async () => {
    setLoadingSchoolBrands(true);
    try {
      const response = await educationAPI.listSchoolBrands({
        Page: 1,
        Size: 100,
        OrderBy: 1,
        SortBy: "Name",
      });
      if (response.success && response.data) {
        setSchoolBrands(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching school brands:", error);
      toast.error("Markalar yüklenirken hata oluştu");
    } finally {
      setLoadingSchoolBrands(false);
    }
  }, []);

  // Fetch cities for school create form
  const fetchCities = useCallback(async () => {
    try {
      const response = await locationsAPI.getCities();
      if (response.success && response.data) {
        setCities((response.data as CitiesResponse["data"]).cities || []);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Şehirler yüklenirken hata oluştu");
    }
  }, []);

  // Fetch districts for school create form
  const fetchDistrictsForForm = useCallback(async (provinceId: string) => {
    try {
      const response = await locationsAPI.getDistricts(provinceId);
      if (response.success && response.data) {
        setDistricts(
          (response.data as DistrictsResponse["data"]).districts || [],
        );
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("İlçeler yüklenirken hata oluştu");
    }
  }, []);

  // Fetch neighborhoods for school create form
  const fetchNeighborhoodsForForm = useCallback(async (districtId: string) => {
    try {
      const response = await locationsAPI.getNeighborhoods(districtId);
      if (response.success && response.data) {
        setNeighborhoods(
          (response.data as NeighborhoodsResponse["data"]).neighborhoods || [],
        );
      }
    } catch (error) {
      console.error("Error fetching neighborhoods:", error);
      toast.error("Mahalleler yüklenirken hata oluştu");
    }
  }, []);

  // Handle school create form success
  const handleSchoolCreateSuccess = useCallback(() => {
    setShowSchoolCreateForm(false);
    setSelectedBrand(null);
    setSelectedProvince(null);
    setSelectedDistrict(null);
    setSelectedNeighborhood(null);
    setSuccessAction("createSchool");
    setShowSuccessModal(true);
  }, []);

  // Load data when school create form opens
  useEffect(() => {
    if (showSchoolCreateForm) {
      fetchSchoolBrandsForForm();
      fetchCities();
    }
  }, [showSchoolCreateForm, fetchSchoolBrandsForForm, fetchCities]);

  // Load schoolId from token
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const schoolIdFromToken = payload.schoolId || payload.SchoolId;
        if (schoolIdFromToken) {
          setSchoolId(schoolIdFromToken);
        }
      } catch (error) {
        console.error("Error parsing token:", error);
      }
    }
  }, []);

  // Load brands when brand management view is active (only once)
  const [hasLoadedBrands, setHasLoadedBrands] = useState(false);

  useEffect(() => {
    console.log(
      "🔄 DEBUG: useEffect - activeView:",
      activeView,
      "hasLoadedBrands:",
      hasLoadedBrands,
    );
    if (activeView === "brands" && !hasLoadedBrands) {
      console.log("🔄 DEBUG: fetchBrands çağrılıyor - brands view aktif");
      fetchBrands(1, ""); // Initial load without search term
      setHasLoadedBrands(true);
    } else if (activeView !== "brands") {
      setHasLoadedBrands(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]); // fetchBrands is stable, no need to include

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleSidebarNavigation = (view: string) => {
    // URL'yi güncelle - activeView useEffect ile otomatik güncellenecek
    navigate(`/${view}`);
  };

  // Memoized handlers for SchoolsView
  const handleSchoolsSuccess = useCallback(
    (action: "createSchool" | "updateSchool" | "deleteSchool") => {
      setSuccessAction(action);
      setShowSuccessModal(true);
    },
    [],
  );

  const handleSchoolsRefresh = useCallback(() => {
    // This will be called when SchoolsView needs to refresh
    // We can add any additional logic here if needed
  }, []);

  // Memoized handlers for EventView
  const handleEventsSuccess = useCallback(
    (action: "createEvent" | "updateEvent" | "deleteEvent") => {
      setSuccessAction(action);
      setShowSuccessModal(true);
    },
    [],
  );

  const handleEventsRefresh = useCallback(() => {
    // This will be called when EventView needs to refresh
    // We can add any additional logic here if needed
  }, []);

  const handleViewEventDetail = useCallback(
    (eventId: string) => {
      navigate(`/events/${eventId}`);
    },
    [navigate],
  );

  const handleBackFromEventDetail = useCallback(() => {
    navigate("/events");
  }, [navigate]);

  // Receivable detail handlers
  const handleViewReceivableDetail = useCallback(
    (receivableId: string) => {
      navigate(`/school-payments/${receivableId}`);
    },
    [navigate],
  );

  const handleBackFromReceivableDetail = useCallback(() => {
    navigate("/school-payments");
  }, [navigate]);

  // Create receivable handlers
  const handleCreateReceivable = useCallback(() => {
    navigate("/school-payments/create");
  }, [navigate]);

  const handleBackFromCreateReceivable = useCallback(() => {
    navigate("/school-payments");
  }, [navigate]);

  // Reset deletedSchoolId after SchoolsView processes it
  useEffect(() => {
    if (deletedSchoolId) {
      // Reset after a short delay to allow SchoolsView to process it
      const timer = setTimeout(() => {
        setDeletedSchoolId(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [deletedSchoolId]);

  const handleSchoolsDeleteConfirm = useCallback((id: string, type: string) => {
    if (type === "school") {
      setDeletingSchoolId(id);
      setShowDeleteModal(true);
    }
  }, []);

  const handleEventsDeleteConfirm = useCallback((id: string, type: string) => {
    if (type === "event") {
      setDeletingSchoolId(id); // Reuse the same state for events
      setShowDeleteModal(true);
    }
  }, []);

  // Helper function to get country flag
  const getCountryFlag = (phoneCode: string): string => {
    const country = countryCodes.find((code) => code.code === phoneCode);
    return country ? country.flag : "🏳️";
  };

  // Load system admins when system-admins view is active
  useEffect(() => {
    if (activeView === "system-admins") {
      fetchSystemAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Load students when students view is active
  useEffect(() => {
    if (activeView === "students") {
      fetchStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]); // fetchStudents is stable

  // Load classrooms when classrooms view is active
  useEffect(() => {
    if (activeView === "classes") {
      fetchClassrooms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]); // fetchClassrooms is stable

  // Load users when users view is active
  useEffect(() => {
    if (activeView === "users") {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]); // fetchUsers is stable

  // Load school users when school-users view is active
  useEffect(() => {
    if (activeView === "school-users") {
      fetchSchoolUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Load parents when parents view is active
  useEffect(() => {
    if (activeView === "parents") {
      fetchParents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  // Close brand dropdown when clicking outside - artık gerekli değil, cmdk otomatik yönetiyor

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);

    // Reset appropriate form based on success action
    if (
      successAction === "createSchool" ||
      successAction === "updateSchool" ||
      successAction === "deleteSchool"
    ) {
      // SchoolsView will handle its own refresh via onSuccess callback
      // No need to refresh here as SchoolsView manages its own data
    } else if (
      successAction === "createStudent" ||
      successAction === "updateStudent" ||
      successAction === "deleteStudent"
    ) {
      // Refresh students list after creating, updating or deleting a student
      if (activeView === "students") {
        fetchStudents(1, studentSearchTerm);
      }
    } else if (
      successAction === "createClassroom" ||
      successAction === "updateClassroom" ||
      successAction === "deleteClassroom"
    ) {
      // Refresh classrooms list after creating, updating or deleting a classroom
      if (activeView === "classes") {
        fetchClassrooms(1, classroomSearchTerm);
      }
    } else if (
      successAction === "createUser" ||
      successAction === "deleteUser" ||
      successAction === "updateSchoolManager"
    ) {
      // Refresh users list after creating, updating, or deleting a user
      if (activeView === "users") {
        fetchUsers(1, userSearchTerm);
      }
    } else if (
      successAction === "createSchoolUser" ||
      successAction === "deleteSchoolUser" ||
      successAction === "updateSchoolUser"
    ) {
      // Refresh school users list after creating, updating, or deleting a school user
      if (activeView === "school-users") {
        fetchSchoolUsers(1, schoolUserSearchTerm);
      }
    } else if (
      successAction === "createSystemAdmin" ||
      successAction === "updateSystemAdmin" ||
      successAction === "deleteSystemAdmin"
    ) {
      // Refresh system admins list after creating, updating, or deleting a system admin
      if (activeView === "system-admins") {
        fetchSystemAdmins(1, systemAdminSearchTerm);
      }
    } else if (
      successAction === "createParent" ||
      successAction === "updateParent" ||
      successAction === "deleteParent"
    ) {
      // Refresh parents list after creating, updating, or deleting a parent
      if (activeView === "parents") {
        fetchParents(1, parentSearchTerm);
      }
    } else if (
      successAction === "createEvent" ||
      successAction === "updateEvent" ||
      successAction === "deleteEvent"
    ) {
      // EventView will handle its own refresh via onSuccess callback
      // No need to refresh here as EventView manages its own data
    } else {
      // Refresh brands list after brand operations
      if (activeView === "brands") {
        fetchBrands(1, searchTerm);
      }
    }
  };

  const handleDeleteClick = (brandId: string) => {
    setDeletingBrandId(brandId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    console.log("🔍 DEBUG: handleDeleteConfirm başladı");
    console.log("🔍 DEBUG: deletingBrandId =", deletingBrandId);

    if (deletingBrandId) {
      // Delete brand
      try {
        setDeleteLoading(true);
        console.log("🔍 DEBUG: API çağrısı yapılıyor...");

        const response = await educationAPI.deleteSchoolBrand(deletingBrandId);

        console.log("🔍 DEBUG: API Response alındı:", response);
        console.log("🔍 DEBUG: response.success =", response.success);
        console.log("🔍 DEBUG: response.message =", response.message);
        console.log("🔍 DEBUG: response.data =", response.data);

        if (response.success) {
          setShowDeleteModal(false);
          setDeletingBrandId(null);
          // fetchBrands() removed - will be called in handleSuccessModalClose
          // Show success message
          setSuccessAction("delete");
          setShowSuccessModal(true);
        } else {
          console.error("Delete brand failed:", response);
          toast.error(
            "Marka silinirken hata oluştu: " +
              (response.message || "Bilinmeyen hata"),
          );
        }
      } catch (error) {
        console.error("Error deleting brand:", error);
        toast.error("Marka silinirken hata oluştu.");
      } finally {
        setDeleteLoading(false);
      }
    } else if (deletingSchoolId) {
      if (activeView === "events") {
        // Delete event
        try {
          setDeleteSchoolLoading(true);
          console.log("🔍 DEBUG: Etkinlik silme API çağrısı yapılıyor...");
          console.log("🔍 DEBUG: deletingSchoolId =", deletingSchoolId);

          const response = await educationAPI.deleteEvent(deletingSchoolId);

          console.log(
            "🔍 DEBUG: Etkinlik silme API Response alındı:",
            response,
          );
          console.log("🔍 DEBUG: response.success =", response.success);
          console.log("🔍 DEBUG: response.message =", response.message);
          console.log("🔍 DEBUG: response.data =", response.data);

          if (response.success) {
            console.log("🎉 DEBUG: Etkinlik başarıyla silindi");
            setShowDeleteModal(false);
            setDeletedSchoolId(deletingSchoolId); // Set the deleted event ID
            setDeletingSchoolId(null);
            // Show success message
            setSuccessAction("deleteEvent");
            setShowSuccessModal(true);
          } else {
            console.error("❌ DEBUG: Etkinlik silme başarısız:", response);
            toast.error(
              "Etkinlik silinirken hata oluştu: " +
                (response.message || "Bilinmeyen hata"),
            );
          }
        } catch (error) {
          console.error("💥 DEBUG: Etkinlik silme exception:", error);
          toast.error("Etkinlik silinirken hata oluştu.");
        } finally {
          setDeleteSchoolLoading(false);
        }
      } else {
        // Delete school
        try {
          setDeleteSchoolLoading(true);
          console.log("🔍 DEBUG: Okul silme API çağrısı yapılıyor...");
          console.log("🔍 DEBUG: deletingSchoolId =", deletingSchoolId);

          const response = await educationAPI.deleteSchool(deletingSchoolId);

          console.log("🔍 DEBUG: Okul silme API Response alındı:", response);
          console.log("🔍 DEBUG: response.success =", response.success);
          console.log("🔍 DEBUG: response.message =", response.message);
          console.log("🔍 DEBUG: response.data =", response.data);

          if (response.success) {
            console.log("🎉 DEBUG: Okul başarıyla silindi");
            setShowDeleteModal(false);
            setDeletedSchoolId(deletingSchoolId); // Set the deleted school ID
            setDeletingSchoolId(null);
            // Show success message
            setSuccessAction("deleteSchool");
            setShowSuccessModal(true);
          } else {
            console.error("❌ DEBUG: Okul silme başarısız:", response);
            toast.error(
              "Okul silinirken hata oluştu: " +
                (response.message || "Bilinmeyen hata"),
            );
          }
        } catch (error) {
          console.error("💥 DEBUG: Okul silme exception:", error);
          toast.error("Okul silinirken hata oluştu.");
        } finally {
          setDeleteSchoolLoading(false);
        }
      }
    } else if (deletingStudentId) {
      // Delete student
      try {
        setDeleteLoading(true);
        console.log("🔍 DEBUG: Öğrenci silme API çağrısı yapılıyor...");
        console.log("🔍 DEBUG: deletingStudentId =", deletingStudentId);

        const response = await educationAPI.deleteStudent(deletingStudentId);

        console.log("🔍 DEBUG: Öğrenci silme API Response alındı:", response);

        if (response.success) {
          console.log("🎉 DEBUG: Öğrenci başarıyla silindi");
          setShowDeleteModal(false);
          setDeletingStudentId(null);
          // Show success message
          setSuccessAction("deleteStudent");
          setShowSuccessModal(true);
        } else {
          console.log("❌ DEBUG: Öğrenci silme başarısız:", response);
          toast.error(
            "Öğrenci silinirken hata oluştu: " +
              (response.message || "Bilinmeyen hata"),
          );
        }
      } catch (error) {
        console.error("💥 DEBUG: Öğrenci silme exception:", error);
        toast.error("Öğrenci silinirken hata oluştu.");
      } finally {
        setDeleteLoading(false);
      }
    } else if (deletingClassroomId) {
      // Delete classroom
      try {
        setDeleteLoading(true);
        console.log("🔍 DEBUG: Sınıf silme API çağrısı yapılıyor...");
        console.log("🔍 DEBUG: deletingClassroomId =", deletingClassroomId);

        const response =
          await educationAPI.deleteClassroom(deletingClassroomId);

        console.log("🔍 DEBUG: Sınıf silme API Response alındı:", response);

        if (response.success) {
          console.log("🎉 DEBUG: Sınıf başarıyla silindi");
          setShowDeleteModal(false);
          setDeletingClassroomId(null);
          // Show success message
          setSuccessAction("deleteClassroom");
          setShowSuccessModal(true);
        } else {
          console.log("❌ DEBUG: Sınıf silme başarısız:", response);
          toast.error(
            "Sınıf silinirken hata oluştu: " +
              (response.message || "Bilinmeyen hata"),
          );
        }
      } catch (error) {
        console.error("💥 DEBUG: Sınıf silme exception:", error);
        toast.error("Sınıf silinirken hata oluştu.");
      } finally {
        setDeleteLoading(false);
      }
    } else if (deletingUserId) {
      // Delete user (school manager or school user)
      try {
        setDeleteLoading(true);

        let response;
        if (activeView === "school-users") {
          console.log(
            "🔍 DEBUG: Okul kullanıcısı silme API çağrısı yapılıyor...",
          );
          console.log("🔍 DEBUG: deletingUserId =", deletingUserId);
          response = await educationAPI.deleteSchoolUser(deletingUserId);
        } else {
          console.log(
            "🔍 DEBUG: Okul yöneticisi silme API çağrısı yapılıyor...",
          );
          console.log("🔍 DEBUG: deletingUserId =", deletingUserId);
          response = await educationAPI.deleteSchoolManager(deletingUserId);
        }

        console.log("🔍 DEBUG: Silme API Response alındı:", response);
        console.log("🔍 DEBUG: response.success =", response.success);
        console.log("🔍 DEBUG: response.message =", response.message);

        if (response.success) {
          console.log("🎉 DEBUG: Kullanıcı başarıyla silindi");
          setShowDeleteModal(false);
          setDeletingUserId(null);
          // Show success message
          setSuccessAction(
            activeView === "school-users" ? "deleteSchoolUser" : "deleteUser",
          );
          setShowSuccessModal(true);
        } else {
          console.error("❌ DEBUG: Kullanıcı silme başarısız:", response);
          const errorMessage =
            activeView === "school-users"
              ? "Okul kullanıcısı silinirken hata oluştu"
              : "Okul yöneticisi silinirken hata oluştu";
          toast.error(
            errorMessage + ": " + (response.message || "Bilinmeyen hata"),
          );
        }
      } catch (error) {
        console.error("💥 DEBUG: Kullanıcı silme exception:", error);
        const errorMessage =
          activeView === "school-users"
            ? "Okul kullanıcısı silinirken hata oluştu."
            : "Okul yöneticisi silinirken hata oluştu.";
        toast.error(errorMessage);
      } finally {
        setDeleteLoading(false);
      }
    } else if (deletingSystemAdminId) {
      // Delete system admin
      try {
        setDeleteLoading(true);
        console.log(
          "🔍 DEBUG: Sistem yöneticisi silme API çağrısı yapılıyor...",
        );
        console.log("🔍 DEBUG: deletingSystemAdminId =", deletingSystemAdminId);

        const response = await educationAPI.deleteSystemAdmin(
          deletingSystemAdminId,
        );

        console.log(
          "🔍 DEBUG: Sistem yöneticisi silme API Response alındı:",
          response,
        );
        console.log("🔍 DEBUG: response.success =", response.success);
        console.log("🔍 DEBUG: response.message =", response.message);

        if (response.success) {
          console.log("🎉 DEBUG: Sistem yöneticisi başarıyla silindi");
          setShowDeleteModal(false);
          setDeletingSystemAdminId(null);
          // Show success message
          setSuccessAction("deleteSystemAdmin");
          setShowSuccessModal(true);
        } else {
          console.error(
            "❌ DEBUG: Sistem yöneticisi silme başarısız:",
            response,
          );
          toast.error(
            "Sistem yöneticisi silinirken hata oluştu: " +
              (response.message || "Bilinmeyen hata"),
          );
        }
      } catch (error) {
        console.error("💥 DEBUG: Sistem yöneticisi silme exception:", error);
        toast.error("Sistem yöneticisi silinirken hata oluştu.");
      } finally {
        setDeleteLoading(false);
      }
    } else if (deletingParentId) {
      // Delete parent
      try {
        setDeleteLoading(true);
        console.log("🔍 DEBUG: Veli silme API çağrısı yapılıyor...");
        console.log("🔍 DEBUG: deletingParentId =", deletingParentId);

        const response = await educationAPI.deleteParent(deletingParentId);

        console.log("🔍 DEBUG: Veli silme API Response alındı:", response);
        console.log("🔍 DEBUG: response.success =", response.success);
        console.log("🔍 DEBUG: response.message =", response.message);

        if (response.success) {
          console.log("🎉 DEBUG: Veli başarıyla silindi");
          setShowDeleteModal(false);
          setDeletingParentId(null);
          // Show success message
          setSuccessAction("deleteParent");
          setShowSuccessModal(true);
        } else {
          console.error("❌ DEBUG: Veli silme başarısız:", response);
          toast.error(
            "Veli silinirken hata oluştu: " +
              (response.message || "Bilinmeyen hata"),
          );
        }
      } catch (error) {
        console.error("💥 DEBUG: Veli silme exception:", error);
        toast.error("Veli silinirken hata oluştu.");
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingBrandId(null);
    setDeletingSchoolId(null);
    setDeletingStudentId(null);
    setDeletingUserId(null);
    setDeletingSystemAdminId(null);
    setDeletingParentId(null);
  };

  // System Admin functions
  const fetchSystemAdmins = async (
    page: number = 1,
    searchTerm: string = "",
  ) => {
    setLoadingSystemAdmins(true);
    try {
      const response = await educationAPI.listSystemAdmins({
        Q: searchTerm,
        Page: page,
        Size: 11,
        OrderBy: 0,
      });

      if (response.success && response.data) {
        const data = response.data as SystemAdminsResponse;
        setSystemAdmins(data.items || []);
        setSystemAdminsPagination({
          currentPage: data.pageNumber || 1,
          totalPages: data.totalPages || 1,
          totalCount: data.totalCount || 0,
          pageSize: 10,
        });
      } else {
        console.error("Failed to fetch system admins:", response);
        toast.error("Sistem yöneticileri yüklenirken hata oluştu");
      }
    } catch (error) {
      console.error("Error fetching system admins:", error);
      toast.error("Sistem yöneticileri yüklenirken hata oluştu");
    } finally {
      setLoadingSystemAdmins(false);
    }
  };

  const handleSystemAdminSearchSubmit = () => {
    fetchSystemAdmins(1, systemAdminSearchTerm);
  };

  const handleEditSystemAdmin = (systemAdminId: string) => {
    setEditingSystemAdminId(systemAdminId);
    setShowSystemAdminEditForm(true);
  };

  const handleDeleteSystemAdmin = (systemAdminId: string) => {
    console.log(
      "Dashboard: Attempting to delete system admin with ID:",
      systemAdminId,
    );
    setDeletingSystemAdminId(systemAdminId);
    setShowDeleteModal(true);
    console.log(
      "Dashboard: State updated - deletingSystemAdminId:",
      systemAdminId,
      "showDeleteModal: true",
    );
  };

  const handleEditBrand = async (brandId: string) => {
    setEditingBrandId(brandId);
    setShowBrandEditForm(true);
  };

  const handleSearch = (value: string) => {
    // Only update input value, never trigger API on change
    setInputValue(value);
  };

  const handleSearchSubmit = () => {
    // Only trigger API when Enter is pressed
    setSearchTerm(inputValue);
    // Reset pagination when searching
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    // Trigger search with the input value
    fetchBrands(1, inputValue);
  };

  // Student handlers
  const handleStudentSearchSubmit = () => {
    fetchStudents(1, studentSearchTerm);
  };

  const handleShowParents = (studentId: string, studentName: string) => {
    setSelectedStudentId(studentId);
    setSelectedStudentName(studentName);
    setShowParentAssignmentForm(true);
  };

  // Parent handlers
  const handleParentSearchSubmit = () => {
    fetchParents(1, parentSearchTerm);
  };

const fetchParents = async (pageNumber: number = 1, searchTerm: string = "") => {
  setLoadingParents(true);
  try {
    const response = await educationAPI.listParents({
      Q: searchTerm,
      Page: pageNumber,
      Size: 10,
      OrderBy: 0,
    });

    if (response.success && response.data) {
      const data = response.data as ParentsResponse;

      setParents(data.items ?? []);

      setParentsPagination({
        pageNumber: data.pageNumber ?? pageNumber,
        totalPages: data.totalPages ?? 1,
        totalCount: data.totalCount ?? 0,
        hasPreviousPage: data.hasPreviousPage ?? ((data.pageNumber ?? pageNumber) > 1),
        hasNextPage: data.hasNextPage ?? ((data.pageNumber ?? pageNumber) < (data.totalPages ?? 1)),
      });

      return;
    }

    setParents([]);
    setParentsPagination({
      pageNumber: 1,
      totalPages: 1,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    });
  } catch (error) {
    console.error("Error fetching parents:", error);
    toast.error("Veliler yüklenirken bir hata oluştu");
    setParents([]);
    setParentsPagination({
      pageNumber: 1,
      totalPages: 1,
      totalCount: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    });
  } finally {
    setLoadingParents(false);
  }
};


  const handleEditParent = (parentId: string) => {
    setEditingParentId(parentId);
    setShowParentEditForm(true);
  };

  const handleDeleteParent = (parentId: string) => {
    setDeletingParentId(parentId);
    setShowDeleteModal(true);
  };

  const handleShowParentDetail = (parentId: string) => {
    setSelectedParentId(parentId);
    setShowParentDetailModal(true);
  };

  const handleShowStudentDetail = (studentId: string) => {
    setSelectedStudentDetailId(studentId);
    setShowStudentDetailModal(true);
  };

  // const resetParentForm = () => {
  //   setParentForm({
  //     firstName: '',
  //     lastName: '',
  //     email: '',
  //     phoneCode: '90',
  //     phoneNumber: '',
  //     relationship: 'Mother',
  //     isPrimary: false
  //   });
  //   setIsEditingParent(false);
  //   setEditingParentId(null);
  // };

  const handleEditStudent = (studentId: string) => {
    setEditingStudentId(studentId);
    setShowStudentEditForm(true);
  };

  const handleDeleteStudent = (studentId: string) => {
    setDeletingStudentId(studentId);
    setShowDeleteModal(true);
  };

  const fetchStudents = async (page: number = 1, searchTerm: string = "") => {
    setLoadingStudents(true);
    try {
      const response = await educationAPI.getStudents({
        Q: searchTerm,
        Page: page,
        Size: 10,
        OrderBy: 0, // Default order
        SortBy: "StudentNumber",
      });

      if (response.success && response.data) {
        setStudents(response.data.items || []);
        setStudentsPagination({
          pageNumber: response.data.pageNumber,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      } else {
        toast.error("Öğrenciler yüklenirken bir hata oluştu");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Öğrenciler yüklenirken bir hata oluştu");
    } finally {
      setLoadingStudents(false);
    }
  };

  // Classroom functions
  const fetchClassrooms = async (page: number = 1, searchTerm: string = "") => {
    setLoadingClassrooms(true);
    try {
      const response = await educationAPI.getClassrooms({
        Q: searchTerm,
        Page: page,
        Size: 10,
        OrderBy: 0, // Default order
        SortBy: "Name", // Default sort by name
      });

      if (response.success && response.data) {
        setClassrooms(response.data.items || []);
        setClassroomsPagination({
          pageNumber: response.data.pageNumber,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      } else {
        toast.error("Sınıflar yüklenirken bir hata oluştu");
      }
    } catch (error) {
      console.error("Error fetching classrooms:", error);
      toast.error("Sınıflar yüklenirken bir hata oluştu");
    } finally {
      setLoadingClassrooms(false);
    }
  };

  const handleEditClassroom = async (classroomId: string) => {
    setEditingClassroomId(classroomId);
    setShowClassroomEditForm(true);
  };

  const handleDeleteClassroom = (classroomId: string) => {
    setDeletingClassroomId(classroomId);
    setShowDeleteModal(true);
  };

  // User handlers
  const handleUserSearchSubmit = () => {
    fetchUsers(1, userSearchTerm);
  };

  const handleEditUser = (userId: string) => {
    console.log("Edit user:", userId);
    // For school managers, open the edit form
    setEditingSchoolManagerId(userId);
    setShowSchoolManagerEditForm(true);
  };

  const handleEditSchoolUser = (userId: string) => {
    console.log("Edit school user:", userId);
    // For school users, open the school user edit form
    setEditingSchoolUserId(userId);
    setShowSchoolUserEditForm(true);
  };

  const handleDeleteUser = (userId: string) => {
    setDeletingUserId(userId);
    setShowDeleteModal(true);
  };

  const fetchUsers = async (
    pageNumber: number = 1,
    searchTerm: string = "",
  ) => {
    setLoadingUsers(true);
    console.log("fetchUsers called with:", { pageNumber, searchTerm });
    try {
      console.log("Calling educationAPI.listSchoolManagers...");
      const response = await educationAPI.listSchoolManagers({
        Q: searchTerm,
        Page: pageNumber,
        Size: 10,
        OrderBy: 0,
      });
      console.log("listSchoolManagers response:", response);

      if (response.success && response.data) {
        // API response structure: { success: true, data: { items: [], pageNumber: 1, ... } }
        const data = response.data;
        setUsers(data.items || []);
        setUsersPagination({
          pageNumber: data.pageNumber,
          totalPages: data.totalPages,
          totalCount: data.totalCount,
          hasPreviousPage: data.hasPreviousPage,
          hasNextPage: data.hasNextPage,
        });
      } else {
        setUsers([]);
        setUsersPagination({
          pageNumber: 1,
          totalPages: 1,
          totalCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      console.error("Error details:", {
        message: (error as any)?.message,
        status: (error as any)?.status,
        response: (error as any)?.response,
      });
      toast.error("Okul Yöneticileri yüklenirken bir hata oluştu");
      setUsers([]);
      setUsersPagination({
        pageNumber: 1,
        totalPages: 1,
        totalCount: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // School Users handlers
  const handleSchoolUserSearchSubmit = () => {
    fetchSchoolUsers(1, schoolUserSearchTerm);
  };

  const fetchSchoolUsers = async (
    pageNumber: number = 1,
    searchTerm: string = "",
  ) => {
    setLoadingSchoolUsers(true);
    console.log("fetchSchoolUsers called with:", { pageNumber, searchTerm });
    try {
      console.log("Calling educationAPI.listSchoolUsers...");
      const response = await educationAPI.listSchoolUsers({
        Q: searchTerm,
        Page: pageNumber,
        Size: 10,
        OrderBy: 0,
      });
      console.log("listSchoolUsers response:", response);

      if (response.success && response.data) {
        // API response structure: { success: true, data: { items: [], pageNumber: 1, ... } }
        const data = response.data;
        // Map API data to table format
        const mappedUsers = (data.items || []).map((item: any) => ({
          id: item.id,
          fullName: `${item.user?.firstName || ""} ${
            item.user?.lastName || ""
          }`.trim(),
          email: item.user?.email || "",
          phoneCode: item.user?.phoneCode || "",
          phoneNumber: item.user?.phoneNumber || "",
          title: item.title || "",
          schoolName: item.school?.name || "",
          userStatus: item.user?.userStatus || 1,
          userType: item.user?.userTypeId ?? item.user?.userType ?? 0,
          isPrimary: item.isPrimary || false,
          createDate: item.createDate || "",
        }));
        setSchoolUsers(mappedUsers);
        setSchoolUsersPagination({
          pageNumber: data.pageNumber,
          totalPages: data.totalPages,
          totalCount: data.totalCount,
          hasPreviousPage: data.hasPreviousPage,
          hasNextPage: data.hasNextPage,
        });
      } else {
        setSchoolUsers([]);
        setSchoolUsersPagination({
          pageNumber: 1,
          totalPages: 1,
          totalCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        });
      }
    } catch (error) {
      console.error("Error fetching school users:", error);
      console.error("Error details:", {
        message: (error as any)?.message,
        status: (error as any)?.status,
        response: (error as any)?.response,
      });
      toast.error("Okul Kullanıcıları yüklenirken bir hata oluştu");
      setSchoolUsers([]);
      setSchoolUsersPagination({
        pageNumber: 1,
        totalPages: 1,
        totalCount: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      });
    } finally {
      setLoadingSchoolUsers(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchBrands(newPage, searchTerm);
    }
  };

  // Component is ready

  const renderSuccessModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
        <div className="p-8 text-center">
          {/* Success Icon */}
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>

          {/* Success Message */}
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Başarılı!
          </h3>
          <p className="text-gray-600 mb-6">
            {successAction === "create" && "Marka başarıyla eklendi."}
            {successAction === "update" && "Marka başarıyla güncellendi."}
            {successAction === "delete" && "Marka başarıyla silindi."}
            {successAction === "createSchool" && "Okul başarıyla eklendi."}
            {successAction === "updateSchool" && "Okul başarıyla güncellendi."}
            {successAction === "deleteSchool" && "Okul başarıyla silindi."}
            {successAction === "createStudent" && "Öğrenci başarıyla eklendi."}
            {successAction === "updateStudent" &&
              "Öğrenci başarıyla güncellendi."}
            {successAction === "deleteStudent" && "Öğrenci başarıyla silindi."}
            {successAction === "createClassroom" && "Sınıf başarıyla eklendi."}
            {successAction === "updateClassroom" &&
              "Sınıf başarıyla güncellendi."}
            {successAction === "deleteClassroom" && "Sınıf başarıyla silindi."}
            {successAction === "createUser" &&
              "Okul Yöneticisi başarıyla eklendi."}
            {successAction === "deleteUser" &&
              "Okul Yöneticisi başarıyla silindi."}
            {successAction === "createSchoolUser" &&
              "Okul Kullanıcısı başarıyla eklendi."}
            {successAction === "deleteSchoolUser" &&
              "Okul Kullanıcısı başarıyla silindi."}
            {successAction === "updateSchoolUser" &&
              "Okul Kullanıcısı başarıyla güncellendi."}
            {successAction === "updateSchoolManager" &&
              "Okul müdürü başarıyla güncellendi."}
            {successAction === "createParent" && "Veli başarıyla eklendi."}
            {successAction === "updateParent" && "Veli başarıyla güncellendi."}
            {successAction === "deleteParent" && "Veli başarıyla silindi."}
          </p>

          {/* OK Button */}
          <Button
            onClick={handleSuccessModalClose}
            className="w-full bg-[#9B51E0] hover:bg-[#8B41D0]"
          >
            Tamam
          </Button>
        </div>
      </div>
    </div>
  );

  const renderDeleteModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
        <div className="p-8 text-center">
          {/* Warning Icon */}
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="w-12 h-12 text-red-600" />
            </div>
          </div>

          {/* Warning Message */}
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {deletingBrandId
              ? "Markayı Sil"
              : deletingSchoolId
                ? "Okulu Sil"
                : deletingStudentId
                  ? "Öğrenciyi Sil"
                  : deletingClassroomId
                    ? "Sınıfı Sil"
                    : deletingUserId
                      ? activeView === "school-users"
                        ? "Okul Kullanıcısını Sil"
                        : "Okul Yöneticisini Sil"
                      : deletingSystemAdminId
                        ? "Sistem Yöneticisini Sil"
                        : deletingParentId
                          ? "Veliyi Sil"
                          : "Sil"}
          </h3>
          <p className="text-gray-600 mb-6">
            {deletingBrandId
              ? "Bu markayı silmek istediğinizden emin misiniz?"
              : deletingSchoolId
                ? "Bu okulu silmek istediğinizden emin misiniz?"
                : deletingStudentId
                  ? "Bu öğrenciyi silmek istediğinizden emin misiniz?"
                  : deletingClassroomId
                    ? "Bu sınıfı silmek istediğinizden emin misiniz?"
                    : deletingUserId
                      ? activeView === "school-users"
                        ? "Bu okul kullanıcısını silmek istediğinizden emin misiniz?"
                        : "Bu okul yöneticisini silmek istediğinizden emin misiniz?"
                      : deletingSystemAdminId
                        ? "Bu sistem yöneticisini silmek istediğinizden emin misiniz?"
                        : deletingParentId
                          ? "Bu veliyi silmek istediğinizden emin misiniz?"
                          : "Bu öğeyi silmek istediğinizden emin misiniz?"}{" "}
            Bu işlem geri alınamaz.
          </p>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={deleteLoading}
              className="flex-1"
            >
              İptal
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleteLoading || deleteSchoolLoading}
              className="flex-1 bg-[#9B51E0] hover:bg-[#8B41D0]"
            >
              {deleteLoading || deleteSchoolLoading ? "Siliniyor..." : "Sil"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemAdminsContent = () => {
    return (
      <div className="space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Sistem yöneticisi ara... (Enter ile ara)"
                  value={systemAdminSearchTerm}
                  onChange={(e) => setSystemAdminSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSystemAdminSearchSubmit();
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Admins Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardDescription>
                {loadingSystemAdmins
                  ? "Yükleniyor..."
                  : `Toplam ${
                      systemAdminsPagination.totalCount || 0
                    } sistem yöneticisi bulundu`}
              </CardDescription>
              <Button
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                onClick={() => setShowSystemAdminCreateForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingSystemAdmins ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                  <p className="text-gray-500">
                    Sistem yöneticileri yükleniyor...
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Yönetici
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          E-posta
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Telefon
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Durum
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Oluşturulma Tarihi
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemAdmins.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="text-center py-8 text-gray-500"
                          >
                            {systemAdminSearchTerm
                              ? "Arama kriterlerine uygun sistem yöneticisi bulunamadı"
                              : "Henüz sistem yöneticisi eklenmemiş"}
                          </td>
                        </tr>
                      ) : (
                        systemAdmins.map((admin) => (
                          <tr
                            key={admin.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-[#9B51E0] rounded-lg flex items-center justify-center">
                                  <span className="text-white font-semibold text-sm">
                                    {admin.firstName
                                      ?.charAt(0)
                                      ?.toUpperCase() || "?"}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {admin.firstName} {admin.lastName}
                                  </span>
                                  <p className="text-sm text-gray-500">
                                    {admin.email}
                                  </p>
                                </div>
                              </div>
                            </td>

                            <td className="py-4 px-4">
                              {admin.email ? (
                                <a
                                  href={`mailto:${admin.email}`}
                                  className="text-[#9B51E0] hover:text-[#8B41D0] text-sm"
                                >
                                  {admin.email}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="py-4 px-4">
                              <span className="text-sm text-gray-600">
                                {admin.phoneCode && admin.phoneNumber
                                  ? `+${admin.phoneCode} ${admin.phoneNumber}`
                                  : "-"}
                              </span>
                            </td>

                            <td className="py-4 px-4">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  admin.userStatus === 1
                                    ? "bg-green-100 text-green-800"
                                    : admin.userStatus === 2
                                      ? "bg-gray-100 text-gray-800"
                                      : admin.userStatus === 3
                                        ? "bg-yellow-100 text-yellow-800"
                                        : admin.userStatus === 4
                                          ? "bg-orange-100 text-orange-800"
                                          : admin.userStatus === 5
                                            ? "bg-red-100 text-red-800"
                                            : admin.userStatus === 6
                                              ? "bg-purple-100 text-purple-800"
                                              : admin.userStatus === 7
                                                ? "bg-gray-100 text-gray-600"
                                                : admin.userStatus === 8
                                                  ? "bg-amber-100 text-amber-800"
                                                  : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {admin.userStatus === 1
                                  ? "Aktif"
                                  : admin.userStatus === 2
                                    ? "Pasif"
                                    : admin.userStatus === 3
                                      ? "Doğrulama Gerekli"
                                      : admin.userStatus === 4
                                        ? "Şifre Belirle"
                                        : admin.userStatus === 5
                                          ? "Kilitli"
                                          : admin.userStatus === 6
                                            ? "Askıya Alınmış"
                                            : admin.userStatus === 7
                                              ? "Devre Dışı"
                                              : admin.userStatus === 8
                                                ? "Şifre Değiştir"
                                                : "Bilinmeyen"}
                              </span>
                            </td>

                            <td className="py-4 px-4">
                              <span className="text-sm text-gray-600">
                                {admin.createdAt
                                  ? new Date(
                                      admin.createdAt,
                                    ).toLocaleDateString("tr-TR", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })
                                  : "-"}
                              </span>
                            </td>

                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() =>
                                    handleEditSystemAdmin(admin.id)
                                  }
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() =>
                                    handleDeleteSystemAdmin(admin.id)
                                  }
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {systemAdminsPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Sayfa {systemAdminsPagination.currentPage} /{" "}
                      {systemAdminsPagination.totalPages}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          fetchSystemAdmins(
                            systemAdminsPagination.currentPage - 1,
                            systemAdminSearchTerm,
                          )
                        }
                        disabled={systemAdminsPagination.currentPage <= 1}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          fetchSystemAdmins(
                            systemAdminsPagination.currentPage + 1,
                            systemAdminSearchTerm,
                          )
                        }
                        disabled={
                          systemAdminsPagination.currentPage >=
                          systemAdminsPagination.totalPages
                        }
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderParentsContent = () => {
    return (
      <div className="space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Veli ara... (Enter ile ara)"
                  value={parentSearchTerm}
                  onChange={(e) => setParentSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleParentSearchSubmit();
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parents Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardDescription>
                  {loadingParents
                    ? "Yükleniyor..."
                    : `Toplam ${
                        parentsPagination.totalCount || 0
                      } veli bulundu`}
                </CardDescription>
              </div>
              <Button
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                onClick={() => setShowParentCreateForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingParents ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                  <p className="text-gray-500">Veliler yükleniyor...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Veli
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          E-posta
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Telefon
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          TC Kimlik No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Meslek
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Yakınlık
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Öğrenci Sayısı
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parents.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="text-center py-8 text-gray-500"
                          >
                            {parentSearchTerm
                              ? "Arama kriterlerine uygun veli bulunamadı"
                              : "Henüz veli eklenmemiş"}
                          </td>
                        </tr>
                      ) : (
                        parents.map((parent) => (
                          <tr key={parent.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-[#9B51E0] flex items-center justify-center">
                                    <span className="text-white font-semibold text-sm">
                                      {parent.fullName
                                        ?.charAt(0)
                                        ?.toUpperCase() || "?"}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {parent.fullName}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {parent.email ? (
                                <a
                                  href={`mailto:${parent.email}`}
                                  className="text-[#9B51E0] hover:text-[#8B41D0] text-sm"
                                >
                                  {parent.email}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {parent.phoneCode && parent.phoneNumber ? (
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">
                                    {getCountryFlag(parent.phoneCode)}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    +{parent.phoneCode}{" "}
                                    {formatPhoneNumber(
                                      parent.phoneCode,
                                      parent.phoneNumber,
                                    )}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 font-mono">
                                {parent.identityNumber || "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {parent.occupation || "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {parent.relationType === "Mother"
                                  ? "Anne"
                                  : parent.relationType === "Father"
                                    ? "Baba"
                                    : parent.relationType === "Guardian"
                                      ? "Vasi"
                                      : parent.relationType === "Other"
                                        ? "Diğer"
                                        : parent.relationType || "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {parent.studentCount || 0}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleShowParentDetail(parent.id)
                                  }
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Detayları Görüntüle"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEditParent(parent.id)}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Düzenle"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteParent(parent.id)}
                                  className="text-red-600 hover:text-red-800 p-1 rounded"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {parentsPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Sayfa {parentsPagination.pageNumber} /{" "}
                      {parentsPagination.totalPages}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          fetchParents(
                            parentsPagination.pageNumber - 1,
                            parentSearchTerm,
                          )
                        }
                        disabled={!parentsPagination.hasPreviousPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          fetchParents(
                            parentsPagination.pageNumber + 1,
                            parentSearchTerm,
                          )
                        }
                        disabled={!parentsPagination.hasNextPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderUsersContent = () => {
    return (
      <div className="space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Okul Yöneticisi ara... (Enter ile ara)"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUserSearchSubmit();
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardDescription>
                {loadingUsers
                  ? "Yükleniyor..."
                  : `Toplam ${
                      usersPagination.totalCount || 0
                    } okul yöneticisi bulundu`}
              </CardDescription>
              <Button
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                onClick={() => setShowUserForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                  <p className="text-gray-500">
                    Okul Yöneticileri yükleniyor...
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Okul Yöneticisi
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          E-posta
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Telefon
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Unvan
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Okul
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Durum
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="text-center py-8 text-gray-500"
                          >
                            {userSearchTerm
                              ? "Arama kriterlerine uygun okul yöneticisi bulunamadı"
                              : "Henüz okul yöneticisi eklenmemiş"}
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr
                            key={user.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-[#9B51E0] rounded-lg flex items-center justify-center">
                                  <span className="text-white font-semibold text-sm">
                                    {user.fullName?.charAt(0)?.toUpperCase() ||
                                      "?"}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {user.fullName || "Bilinmeyen"}
                                  </span>
                                  <p className="text-sm text-gray-500">
                                    {user.title}
                                  </p>
                                </div>
                              </div>
                            </td>

                            <td className="py-4 px-4">
                              {user.email ? (
                                <a
                                  href={`mailto:${user.email}`}
                                  className="text-[#9B51E0] hover:text-[#8B41D0] text-sm"
                                >
                                  {user.email}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="py-4 px-4">
                              {user.phoneCode && user.phoneNumber ? (
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">
                                    {getCountryFlag(user.phoneCode)}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    +{user.phoneCode}{" "}
                                    {formatPhoneNumber(
                                      user.phoneCode,
                                      user.phoneNumber,
                                    )}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="py-4 px-4">
                              <span className="text-sm text-gray-600">
                                {user.title}
                              </span>
                            </td>

                            <td className="py-4 px-4">
                              <span className="text-sm text-gray-600">
                                {user.schoolName}
                              </span>
                            </td>

                            <td className="py-4 px-4">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  (user as any).status === "Active"
                                    ? "bg-green-100 text-green-800"
                                    : (user as any).status === "Inactive"
                                      ? "bg-gray-100 text-gray-800"
                                      : (user as any).status ===
                                          "RequiredVerify"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : (user as any).status ===
                                            "RequiredPassword"
                                          ? "bg-orange-100 text-orange-800"
                                          : (user as any).status === "LockedOut"
                                            ? "bg-red-100 text-red-800"
                                            : (user as any).status ===
                                                "Suspended"
                                              ? "bg-purple-100 text-purple-800"
                                              : (user as any).status ===
                                                  "Disabled"
                                                ? "bg-gray-100 text-gray-600"
                                                : (user as any).status ===
                                                    "MustChangePassword"
                                                  ? "bg-amber-100 text-amber-800"
                                                  : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {(user as any).status === "Active"
                                  ? "Aktif"
                                  : (user as any).status === "Inactive"
                                    ? "Pasif"
                                    : (user as any).status === "RequiredVerify"
                                      ? "Doğrulama Gerekli"
                                      : (user as any).status ===
                                          "RequiredPassword"
                                        ? "Şifre Belirle"
                                        : (user as any).status === "LockedOut"
                                          ? "Kilitli"
                                          : (user as any).status === "Suspended"
                                            ? "Askıya Alınmış"
                                            : (user as any).status ===
                                                "Disabled"
                                              ? "Devre Dışı"
                                              : (user as any).status ===
                                                  "MustChangePassword"
                                                ? "Şifre Değiştir"
                                                : "Bilinmeyen"}
                              </span>
                            </td>

                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleEditUser(user.id)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteUser(user.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {usersPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Sayfa {usersPagination.pageNumber} /{" "}
                      {usersPagination.totalPages}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          fetchUsers(
                            usersPagination.pageNumber - 1,
                            userSearchTerm,
                          )
                        }
                        disabled={!usersPagination.hasPreviousPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          fetchUsers(
                            usersPagination.pageNumber + 1,
                            userSearchTerm,
                          )
                        }
                        disabled={!usersPagination.hasNextPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderSchoolUsersContent = () => {
    return (
      <div className="space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Okul Kullanıcısı ara... (Enter ile ara)"
                  value={schoolUserSearchTerm}
                  onChange={(e) => setSchoolUserSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSchoolUserSearchSubmit();
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* School Users Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardDescription>
                  {loadingSchoolUsers
                    ? "Yükleniyor..."
                    : `Toplam ${
                        schoolUsersPagination.totalCount || 0
                      } okul kullanıcısı bulundu`}
                </CardDescription>
              </div>
              <Button
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                onClick={() => setShowSchoolUserForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingSchoolUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                  <p className="text-gray-500">
                    Okul Kullanıcıları yükleniyor...
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Okul Kullanıcısı
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          E-posta
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Telefon
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Kullanıcı Tipi
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Oluşturulma Tarihi
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {schoolUsers.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="text-center py-8 text-gray-500"
                          >
                            {schoolUserSearchTerm
                              ? "Arama kriterlerine uygun okul kullanıcısı bulunamadı"
                              : "Henüz okul kullanıcısı eklenmemiş"}
                          </td>
                        </tr>
                      ) : (
                        schoolUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div
                                    className={`h-10 w-10 rounded-lg flex items-center justify-center ${getUserTypeBadgeBgColor(
                                      (user as any).userType,
                                    )} text-white`}
                                  >
                                    {getUserTypeIcon(
                                      (user as any).userType,
                                      "large",
                                    )}
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {user.fullName || "Bilinmeyen"}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {user.title}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {user.email ? (
                                <a
                                  href={`mailto:${user.email}`}
                                  className="text-[#9B51E0] hover:text-[#8B41D0] text-sm"
                                >
                                  {user.email}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {user.phoneCode && user.phoneNumber ? (
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">
                                    {getCountryFlag(user.phoneCode)}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    +{user.phoneCode}{" "}
                                    {formatPhoneNumber(
                                      user.phoneCode,
                                      user.phoneNumber,
                                    )}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUserTypeBadgeColor(
                                  (user as any).userType,
                                )}`}
                              >
                                {getUserTypeText((user as any).userType)}
                              </span>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {(user as any).createDate
                                  ? new Date(
                                      (user as any).createDate,
                                    ).toLocaleDateString("tr-TR")
                                  : "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  (user as any).userStatus === UserStatus.Active
                                    ? "bg-green-100 text-green-800"
                                    : (user as any).userStatus ===
                                        UserStatus.Inactive
                                      ? "bg-gray-100 text-gray-800"
                                      : (user as any).userStatus ===
                                          UserStatus.RequiresVerification
                                        ? "bg-yellow-100 text-yellow-800"
                                        : (user as any).userStatus ===
                                            UserStatus.RequiredPassword
                                          ? "bg-orange-100 text-orange-800"
                                          : (user as any).userStatus ===
                                              UserStatus.LockedOut
                                            ? "bg-red-100 text-red-800"
                                            : (user as any).userStatus ===
                                                UserStatus.Suspended
                                              ? "bg-purple-100 text-purple-800"
                                              : (user as any).userStatus ===
                                                  UserStatus.Disabled
                                                ? "bg-gray-100 text-gray-600"
                                                : (user as any).userStatus ===
                                                    UserStatus.MustChangePassword
                                                  ? "bg-blue-100 text-blue-800"
                                                  : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {(user as any).userStatus === UserStatus.Active
                                  ? "Aktif"
                                  : (user as any).userStatus ===
                                      UserStatus.Inactive
                                    ? "Pasif"
                                    : (user as any).userStatus ===
                                        UserStatus.RequiresVerification
                                      ? "Doğrulama Gerekli"
                                      : (user as any).userStatus ===
                                          UserStatus.RequiredPassword
                                        ? "Şifre Gerekli"
                                        : (user as any).userStatus ===
                                            UserStatus.LockedOut
                                          ? "Kilitli"
                                          : (user as any).userStatus ===
                                              UserStatus.Suspended
                                            ? "Askıya Alındı"
                                            : (user as any).userStatus ===
                                                UserStatus.Disabled
                                              ? "Devre Dışı"
                                              : (user as any).userStatus ===
                                                  UserStatus.MustChangePassword
                                                ? "Şifre Değiştirmeli"
                                                : "Bilinmeyen"}
                              </span>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEditSchoolUser(user.id)}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Düzenle"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600 hover:text-red-800 p-1 rounded"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {schoolUsersPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Sayfa {schoolUsersPagination.pageNumber} /{" "}
                      {schoolUsersPagination.totalPages}
                    </div>
                    <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        const prev = Math.max(1, schoolUsersPagination.pageNumber - 1);
                        fetchSchoolUsers(prev, schoolUserSearchTerm);
                      }}
                      disabled={schoolUsersPagination.pageNumber <= 1}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const next = Math.min(
                            schoolUsersPagination.totalPages,
                            schoolUsersPagination.pageNumber + 1
                          );
                          fetchSchoolUsers(next, schoolUserSearchTerm);
                        }}
                        disabled={schoolUsersPagination.pageNumber >= schoolUsersPagination.totalPages}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderStudentsContent = () => {
    return (
      <div className="space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Öğrenci ara... (Enter ile ara)"
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleStudentSearchSubmit();
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Students Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardDescription>
                  {loadingStudents
                    ? "Yükleniyor..."
                    : `Toplam ${
                        studentsPagination.totalCount || 0
                      } öğrenci bulundu`}
                </CardDescription>
              </div>
              <Button
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                onClick={() => setShowStudentCreateForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingStudents ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                  <p className="text-gray-500">Öğrenciler yükleniyor...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ad Soyad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Öğrenci No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cinsiyet
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Doğum Tarihi
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Veliler
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Durum
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {students.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="text-center py-8 text-gray-500"
                          >
                            {studentSearchTerm
                              ? "Arama kriterlerine uygun öğrenci bulunamadı"
                              : "Henüz öğrenci eklenmemiş"}
                          </td>
                        </tr>
                      ) : (
                        students.map((student) => (
                          <tr
                            key={student.studentId}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div
                                    className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                      student.gender === 1
                                        ? "bg-[rgb(102,153,238)]"
                                        : student.gender === 2
                                          ? "bg-[rgb(247,120,120)]"
                                          : "bg-[#9B51E0]"
                                    }`}
                                  >
                                    <User className="w-5 h-5 text-white" />
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {student.firstName} {student.lastName}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 font-mono">
                                {student.studentNumber || "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {student.gender === 1
                                  ? "Erkek"
                                  : student.gender === 2
                                    ? "Kadın"
                                    : student.gender === 3
                                      ? "Diğer"
                                      : "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {student.birthDate
                                  ? new Date(
                                      student.birthDate,
                                    ).toLocaleDateString("tr-TR")
                                  : "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="space-y-1">
                                {student.parents &&
                                student.parents.length > 0 ? (
                                  student.parents.map((parent: ParentDto) => (
                                    <div
                                      key={parent.parentId}
                                      className="text-sm text-gray-900"
                                    >
                                      {parent.fullName}
                                    </div>
                                  ))
                                ) : (
                                  <span className="text-gray-400 text-sm">
                                    Veli atanmamış
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  student.status === 1
                                    ? "bg-green-100 text-green-800"
                                    : student.status === 2
                                      ? "bg-blue-100 text-blue-800"
                                      : student.status === 3
                                        ? "bg-yellow-100 text-yellow-800"
                                        : student.status === 4
                                          ? "bg-red-100 text-red-800"
                                          : student.status === 5
                                            ? "bg-gray-100 text-gray-800"
                                            : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {student.status === 1
                                  ? "Aktif"
                                  : student.status === 2
                                    ? "Mezun"
                                    : student.status === 3
                                      ? "Nakil"
                                      : student.status === 4
                                        ? "Askıya Alındı"
                                        : student.status === 5
                                          ? "Ayrıldı"
                                          : student.status || "Bilinmeyen"}
                              </span>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleShowStudentDetail(student.studentId)
                                  }
                                  className="text-green-600 hover:text-green-800 p-1 rounded"
                                  title="Detayları Görüntüle"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleEditStudent(student.studentId)
                                  }
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Düzenle"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleShowParents(
                                      student.studentId,
                                      `${student.firstName} ${student.lastName}`,
                                    )
                                  }
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Velileri Görüntüle"
                                >
                                  <Users className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteStudent(student.studentId)
                                  }
                                  className="text-red-600 hover:text-red-800 p-1 rounded"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {studentsPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Sayfa {studentsPagination.pageNumber} /{" "}
                      {studentsPagination.totalPages}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          fetchStudents(
                            studentsPagination.pageNumber - 1,
                            studentSearchTerm,
                          )
                        }
                        disabled={!studentsPagination.hasPreviousPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          fetchStudents(
                            studentsPagination.pageNumber + 1,
                            studentSearchTerm,
                          )
                        }
                        disabled={!studentsPagination.hasNextPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderClassroomsContent = () => {
    return (
      <div className="space-y-6">
        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Sınıf ara... (Enter ile ara)"
                  value={classroomSearchTerm}
                  onChange={(e) => setClassroomSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      fetchClassrooms(1, classroomSearchTerm);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Classrooms Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardDescription>
                  {loadingClassrooms
                    ? "Yükleniyor..."
                    : `Toplam ${
                        classroomsPagination.totalCount || 0
                      } sınıf bulundu`}
                </CardDescription>
              </div>
              <Button
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                onClick={() => setShowClassroomCreateForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Ekle
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingClassrooms ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                  <p className="text-gray-500">Sınıflar yükleniyor...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sınıf Adı
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sınıf Seviyesi
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Şube
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Kişi Sayısı
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Yıl
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          İşlemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {classrooms.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-8 text-gray-500"
                          >
                            {classroomSearchTerm
                              ? "Arama kriterlerine uygun sınıf bulunamadı"
                              : "Henüz sınıf eklenmemiş"}
                          </td>
                        </tr>
                      ) : (
                        classrooms.map((classroom) => (
                          <tr key={classroom.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-lg bg-[#9B51E0] flex items-center justify-center">
                                    <span className="text-white font-semibold text-sm">
                                      {classroom.name
                                        ?.charAt(0)
                                        ?.toUpperCase() || "?"}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {classroom.name}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {classroom.grade || "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {classroom.section || "-"}
                              </div>
                            </td>
                            
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {classroom.totalStudent || "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {classroom.year || "-"}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleEditClassroom(classroom.id)
                                  }
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Düzenle"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteClassroom(classroom.id)
                                  }
                                  className="text-red-600 hover:text-red-800 p-1 rounded"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {classroomsPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-700">
                      Sayfa {classroomsPagination.pageNumber} /{" "}
                      {classroomsPagination.totalPages}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          fetchClassrooms(
                            classroomsPagination.pageNumber - 1,
                            classroomSearchTerm,
                          )
                        }
                        disabled={!classroomsPagination.hasPreviousPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          fetchClassrooms(
                            classroomsPagination.pageNumber + 1,
                            classroomSearchTerm,
                          )
                        }
                        disabled={!classroomsPagination.hasNextPage}
                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4 rotate-180" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderBrandManagementTable = () => (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Marka ara... (Enter ile ara)"
                value={inputValue}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchSubmit();
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brands Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardDescription>
                {loading
                  ? "Yükleniyor..."
                  : `Toplam ${pagination.totalCount} marka bulundu`}
              </CardDescription>
            </div>
            <Button
              className="bg-[#9B51E0] hover:bg-[#8B41D0]"
              onClick={() => setShowBrandCreateForm(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                <p className="text-gray-500">Markalar yükleniyor...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Ad
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        E-posta
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Telefon
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Durum
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Facebook
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Instagram
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        Website
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {brands.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="py-8 text-center text-gray-500"
                        >
                          {searchTerm
                            ? "Arama kriterlerine uygun marka bulunamadı"
                            : "Henüz marka eklenmemiş"}
                        </td>
                      </tr>
                    ) : (
                      brands.map((brand) => (
                        <tr
                          key={brand.id}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          {/* Hidden Id for internal use */}
                          <td className="hidden">
                            <input type="hidden" value={brand.id} />
                          </td>

                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <span className="text-[#9B51E0] font-semibold text-sm">
                                  {brand.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="font-medium text-gray-900">
                                {brand.name}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            {brand.email ? (
                              <a
                                href={`mailto:${brand.email}`}
                                className="text-[#9B51E0] hover:text-[#8B41D0] text-sm"
                              >
                                {brand.email}
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {brand.phoneCode && brand.phoneNumber ? (
                              <div className="flex items-center space-x-2">
                                <span className="text-lg">
                                  {(() => {
                                    // Get flag emoji based on phone code
                                    const flagMap: { [key: string]: string } = {
                                      "90": "🇹🇷",
                                      "1": "🇺🇸",
                                      "44": "🇬🇧",
                                      "49": "🇩🇪",
                                      "33": "🇫🇷",
                                      "39": "🇮🇹",
                                      "34": "🇪🇸",
                                      "31": "🇳🇱",
                                      "32": "🇧🇪",
                                      "46": "🇸🇪",
                                      "47": "🇳🇴",
                                      "45": "🇩🇰",
                                      "358": "🇫🇮",
                                      "7": "🇷🇺",
                                      "86": "🇨🇳",
                                      "81": "🇯🇵",
                                      "82": "🇰🇷",
                                      "91": "🇮🇳",
                                      "971": "🇦🇪",
                                      "966": "🇸🇦",
                                      "20": "🇪🇬",
                                      "27": "🇿🇦",
                                      "55": "🇧🇷",
                                      "54": "🇦🇷",
                                      "52": "🇲🇽",
                                      "61": "🇦🇺",
                                      "64": "🇳🇿",
                                    };
                                    return flagMap[brand.phoneCode] || "🌍";
                                  })()}
                                </span>
                                <span className="text-sm text-gray-600">
                                  {formatPhoneNumber(
                                    brand.phoneCode,
                                    brand.phoneNumber,
                                  )}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                brand.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {brand.isActive ? "Aktif" : "Pasif"}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            {brand.facebook ? (
                              <a
                                href={
                                  brand.facebook.startsWith("http")
                                    ? brand.facebook
                                    : `https://${brand.facebook}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors duration-200"
                                title={brand.facebook}
                              >
                                <Facebook className="w-4 h-4 text-[#9B51E0]" />
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-center">
                            {brand.instagram ? (
                              <a
                                href={
                                  brand.instagram.startsWith("http")
                                    ? brand.instagram
                                    : `https://${brand.instagram}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-8 h-8 bg-pink-100 hover:bg-pink-100 rounded-full transition-colors duration-200"
                                title={brand.instagram}
                              >
                                <Instagram className="w-4 h-4 text-pink-500" />
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {brand.webSite ? (
                              <a
                                href={brand.webSite}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center space-x-2 text-[#9B51E0] hover:text-[#8B41D0] text-sm"
                              >
                                <Globe className="w-5 h-5" />
                                <span>
                                  {brand.webSite.replace(/^https?:\/\//, "")}
                                </span>
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleEditBrand(brand.id)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteClick(brand.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Sayfa {pagination.pageNumber} / {pagination.totalPages}(
                    {pagination.totalCount} toplam marka)
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handlePageChange(pagination.pageNumber - 1)
                      }
                      disabled={!pagination.hasPreviousPage}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Önceki
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handlePageChange(pagination.pageNumber + 1)
                      }
                      disabled={!pagination.hasNextPage}
                    >
                      Sonraki
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderDashboardContent = () => {
    if (loadingDashboardStats) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B51E0]"></div>
        </div>
      );
    }

    return (
      <>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Toplam Okullar */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Toplam Okullar
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {dashboardStats?.totalSchool ?? 0}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Sistemdeki toplam okul sayısı
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Toplam Kullanıcılar */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Toplam Kullanıcılar
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {dashboardStats?.totalUsers ?? 0}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Kayıtlı kullanıcı sayısı
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Toplam Öğrenciler */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Toplam Öğrenciler
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {dashboardStats?.totalStudents ?? 0}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Sistemdeki öğrenci sayısı
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Aktif Sınıflar */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Aktif Sınıflar
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {dashboardStats?.activeClassrooms ?? 0}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Aktif sınıf sayısı
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Yaklaşan Etkinlikler */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Yaklaşan Etkinlikler
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {dashboardStats?.upcomingEvents ?? 0}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Planlanmış etkinlik sayısı
                  </p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Son Kayıtlar */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Son Kayıtlar
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {dashboardStats?.recentRegistrations ?? 0}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Yeni etkinlik kayıtları
                  </p>
                </div>
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-pink-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions & System Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Hızlı İşlemler</CardTitle>
              <CardDescription>Sık kullanılan işlemler</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="h-20 flex-col justify-center"
                  onClick={() => handleSidebarNavigation("schools")}
                >
                  <Building2 className="w-6 h-6 mb-2 text-blue-600" />
                  <span className="text-sm">Okulları Görüntüle</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col justify-center"
                  onClick={() => handleSidebarNavigation("students")}
                >
                  <GraduationCap className="w-6 h-6 mb-2 text-green-600" />
                  <span className="text-sm">Öğrenciler</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col justify-center"
                  onClick={() => handleSidebarNavigation("events")}
                >
                  <Calendar className="w-6 h-6 mb-2 text-indigo-600" />
                  <span className="text-sm">Etkinlikler</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-20 flex-col justify-center"
                  onClick={() => handleSidebarNavigation("school-payments")}
                >
                  <Award className="w-6 h-6 mb-2 text-orange-600" />
                  <span className="text-sm">Ödemeler</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sistem Özeti</CardTitle>
              <CardDescription>
                Genel sistem durumu ve istatistikleri
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Okullar
                      </p>
                      <p className="text-xs text-gray-500">
                        Toplam okul sayısı
                      </p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {dashboardStats?.totalSchool ?? 0}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Sınıflar
                      </p>
                      <p className="text-xs text-gray-500">Aktif sınıflar</p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {dashboardStats?.activeClassrooms ?? 0}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Etkinlikler
                      </p>
                      <p className="text-xs text-gray-500">
                        Yaklaşan etkinlikler
                      </p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {dashboardStats?.upcomingEvents ?? 0}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Yeni Kayıtlar
                      </p>
                      <p className="text-xs text-gray-500">
                        Son etkinlik kayıtları
                      </p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {dashboardStats?.recentRegistrations ?? 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar onNavigation={handleSidebarNavigation} activeView={activeView} />

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {activeView === "brands"
                  ? "Markalar"
                  : activeView === "schools"
                    ? "Okullar"
                    : activeView === "events"
                      ? "Etkinlikler"
                      : activeView === "school-payments"
                        ? "Okul Ödemeleri"
                        : activeView === "reports"
                          ? "Raporlar"
                          : activeView === "settings"
                            ? "Ayarlar"
                            : activeView === "account"
                              ? "Hesabım"
                            : activeView === "support"
                              ? "Yardım Ve Destek"
                              : activeView === "students"
                                ? "Öğrenciler"
                                : activeView === "parents"
                                  ? "Veliler"
                                  : activeView === "classes"
                                    ? "Sınıflar"
                                    : activeView === "users"
                                      ? "Okul Yöneticileri"
                                      : activeView === "school-users"
                                        ? "Okul Kullanıcıları"
                                        : activeView === "system-users"
                                          ? "Sistem Kullanıcıları"
                                          : activeView === "system-admins"
                                            ? "Sistem Yöneticileri"
                                            : "Dashboard"}
              </h1>
              <p className="text-gray-600">
                {activeView === "brands"
                  ? "Sistemdeki tüm markaları görüntüleyin ve yönetin"
                  : activeView === "schools"
                    ? "Sistemdeki tüm okulları görüntüleyin ve yönetin"
                    : activeView === "events"
                      ? "Sistemdeki tüm etkinlikleri görüntüleyin ve yönetin"
                      : activeView === "school-payments"
                        ? "Sistemdeki tüm okul ödemelerini görüntüleyin ve yönetin"
                        : activeView === "reports"
                          ? "Finansal raporları görüntüleyin ve indirin"
                          : activeView === "settings"
                            ? "Ödeme tiplerini yönetin"
                            : activeView === "account"
                              ? "Hesap bilgilerinizi görüntüleyin ve düzenleyin"
                            : activeView === "support"
                              ? "Yardım ve Destek Alın"
                              : activeView === "students"
                                ? "Sistemdeki tüm öğrencileri görüntüleyin ve yönetin"
                                : activeView === "parents"
                                  ? "Sistemdeki tüm velileri görüntüleyin ve yönetin"
                                  : activeView === "classes"
                                    ? "Sistemdeki tüm sınıfları görüntüleyin ve yönetin"
                                    : activeView === "users"
                                      ? "Sistemdeki tüm kullanıcıları görüntüleyin ve yönetin"
                                      : activeView === "school-users"
                                        ? "Sistemdeki tüm okul kullanıcılarını görüntüleyin ve yönetin"
                                        : activeView === "system-users"
                                          ? "Sistemdeki tüm sistem kullanıcılarını görüntüleyin ve yönetin"
                                          : activeView === "system-admins"
                                            ? "Sistemdeki tüm sistem yöneticilerini görüntüleyin ve yönetin"
                                            : "Hoş geldiniz! Sistem genel durumu"}
              </p>
            </div>
            {/* User Profile Dropdown */}
            <div className="relative user-dropdown">
              <button
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {userLoading
                      ? "..."
                      : currentUser?.fullName || "Authenticated User"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {userLoading
                      ? "Yükleniyor..."
                      : translateRole(currentUser?.role)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white font-semibold text-sm">
                    {userLoading
                      ? "..."
                      : (currentUser?.fullName?.charAt(0) || "A").toUpperCase()}
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                    userDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {userDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  {/* User Info Section */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-white font-semibold text-lg">
                          {userLoading
                            ? "..."
                            : (
                                currentUser?.fullName?.charAt(0) || "A"
                              ).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {userLoading
                            ? "Yükleniyor..."
                            : currentUser?.fullName || "Authenticated User"}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {userLoading
                            ? "..."
                            : currentUser?.email || "admin@sparkion.com"}
                        </p>
                        <div className="flex items-center mt-1 space-x-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {userLoading
                              ? "..."
                              : translateRole(currentUser?.role)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setUserDropdownOpen(false);
                        handleSidebarNavigation("account");
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Hesabım
                    </button>
                    <button
                      onClick={() => {
                        setUserDropdownOpen(false);
                        handleSidebarNavigation("support");
                      }}                      
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                    >
                      <svg
                        className="w-4 h-4 mr-3 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Yardım & Destek
                    </button> 
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 my-2"></div>

                  {/* Logout Section */}
                  <div className="py-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                    >
                      <svg
                        className="w-4 h-4 mr-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Çıkış Yap
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-6">
          {activeView === "brands" ? (
            renderBrandManagementTable()
          ) : activeView === "schools" ? (
            <SchoolsView
              onSuccess={handleSchoolsSuccess}
              onDeleteConfirm={handleSchoolsDeleteConfirm}
              onRefresh={handleSchoolsRefresh}
              deletedSchoolId={deletedSchoolId}
            />
          ) : activeView === "events" ? (
            routeId ? (
              <EventDetailView
                eventId={routeId}
                schoolId={schoolId}
                onBack={handleBackFromEventDetail}
                onDeleteConfirm={handleEventsDeleteConfirm}
                onSuccess={handleEventsSuccess}
              />
            ) : (
              <EventView
                onSuccess={handleEventsSuccess}
                onDeleteConfirm={handleEventsDeleteConfirm}
                onRefresh={handleEventsRefresh}
                onViewDetail={handleViewEventDetail}
                deletedEventId={deletedSchoolId}
                schoolId={schoolId}
              />
            )
          ) : activeView === "school-payments" ? (
            location.pathname === "/school-payments/create" ? (
              <CreateReceivableView
                onBack={handleBackFromCreateReceivable}
                onSuccess={handleBackFromCreateReceivable}
              />
            ) : routeId ? (
              <ReceivableDetailView
                receivableId={routeId}
                onBack={handleBackFromReceivableDetail}
              />
            ) : (
              <ReceivablesView
                onViewDetail={handleViewReceivableDetail}
                onCreateNew={handleCreateReceivable}
              />
            )
          ) : activeView === "settings" ? (
            <SettingsView />
          ) : activeView === "account" ? (
            <AccountView />
          ) : activeView === "support" ? (
            <HelpSupportPage />
          ) : activeView === "students" ? (
            renderStudentsContent()
          ) : activeView === "parents" ? (
            renderParentsContent()
          ) : activeView === "classes" ? (
            renderClassroomsContent()
          ) : activeView === "users" ? (
            renderUsersContent()
          ) : activeView === "school-users" ? (
            renderSchoolUsersContent()
          ) : activeView === "system-admins" ? (
            renderSystemAdminsContent()
          ) : (
            renderDashboardContent()
          )}
        </main>
      </div>
      {showBrandCreateForm && (
        <BrandCreateForm
          isOpen={showBrandCreateForm}
          onClose={() => setShowBrandCreateForm(false)}
          onSuccess={() => {
            setSuccessAction("create");
            setShowSuccessModal(true);
          }}
        />
      )}
      {showBrandEditForm && (
        <BrandEditForm
          isOpen={showBrandEditForm}
          onClose={() => setShowBrandEditForm(false)}
          onSuccess={() => {
            setSuccessAction("update");
            setShowSuccessModal(true);
          }}
          editingBrandId={editingBrandId}
        />
      )}
      {showStudentCreateForm && (
        <StudentCreateForm
          isOpen={showStudentCreateForm}
          onClose={() => setShowStudentCreateForm(false)}
          onSuccess={() => {
            setShowStudentCreateForm(false);
            setSuccessAction("createStudent");
            setShowSuccessModal(true);
            fetchStudents(1, studentSearchTerm);
          }}
          schoolId={schoolId}
        />
      )}
      {showStudentEditForm && (
        <StudentEditForm
          isOpen={showStudentEditForm}
          onClose={() => {
            setShowStudentEditForm(false);
            setEditingStudentId(null);
          }}
          studentId={editingStudentId}
          onSuccess={() => {
            setShowStudentEditForm(false);
            setEditingStudentId(null);
            setSuccessAction("updateStudent");
            setShowSuccessModal(true);
            fetchStudents(1, studentSearchTerm);
          }}
          schoolId={schoolId}
        />
      )}
      {showParentAssignmentForm && selectedStudentId && (
        <ParentAssignmentForm
          isOpen={showParentAssignmentForm}
          onClose={() => {
            setShowParentAssignmentForm(false);
            setSelectedStudentId(null);
            setSelectedStudentName("");
          }}
          studentId={selectedStudentId}
          studentName={selectedStudentName}
          onSuccess={() => {
            // Refresh students list to show updated parent count
            fetchStudents(1, studentSearchTerm);
          }}
        />
      )}
      {showClassroomCreateForm && (
        <ClassroomCreateForm
          isOpen={showClassroomCreateForm}
          onClose={() => setShowClassroomCreateForm(false)}
          onSuccess={() => {
            setSuccessAction("createClassroom");
            setShowSuccessModal(true);
          }}
        />
      )}
      {showSchoolCreateForm && (
        <SchoolCreateForm
          isOpen={showSchoolCreateForm}
          onClose={() => {
            setShowSchoolCreateForm(false);
            setSelectedBrand(null);
            setSelectedProvince(null);
            setSelectedDistrict(null);
            setSelectedNeighborhood(null);
          }}
          onSuccess={handleSchoolCreateSuccess}
          selectedBrand={selectedBrand}
          onBrandChange={setSelectedBrand}
          schoolBrands={schoolBrands}
          loadingSchoolBrands={loadingSchoolBrands}
          cities={cities}
          districts={districts}
          neighborhoods={neighborhoods}
          selectedProvince={selectedProvince}
          selectedDistrict={selectedDistrict}
          selectedNeighborhood={selectedNeighborhood}
          onProvinceChange={setSelectedProvince}
          onDistrictChange={setSelectedDistrict}
          onNeighborhoodChange={setSelectedNeighborhood}
          onFetchDistricts={fetchDistrictsForForm}
          onFetchNeighborhoods={fetchNeighborhoodsForForm}
        />
      )}
      {showClassroomEditForm && (
        <ClassroomEditForm
          isOpen={showClassroomEditForm}
          onClose={() => setShowClassroomEditForm(false)}
          onSuccess={() => {
            setSuccessAction("updateClassroom");
            setShowSuccessModal(true);
          }}
          editingClassroomId={editingClassroomId}
        />
      )}
      {showUserForm && (
        <SchoolManagerCreateForm
          isOpen={showUserForm}
          onClose={() => setShowUserForm(false)}
          onSuccess={() => {
            setSuccessAction("createUser");
            setShowSuccessModal(true);
          }}
        />
      )}
      {showSchoolUserForm && (
        <SchoolUserCreateForm
          isOpen={showSchoolUserForm}
          onClose={() => setShowSchoolUserForm(false)}
          onSuccess={() => {
            setSuccessAction("createSchoolUser");
            setShowSuccessModal(true);
          }}
        />
      )}
      {showSchoolManagerEditForm && (
        <SchoolManagerEditForm
          isOpen={showSchoolManagerEditForm}
          onClose={() => {
            setShowSchoolManagerEditForm(false);
            setEditingSchoolManagerId(null);
          }}
          schoolManagerId={editingSchoolManagerId}
          onSuccess={() => {
            setSuccessAction("updateSchoolManager");
            setShowSuccessModal(true);
          }}
        />
      )}

      {showSchoolUserEditForm && (
        <SchoolUserEditForm
          isOpen={showSchoolUserEditForm}
          onClose={() => {
            setShowSchoolUserEditForm(false);
            setEditingSchoolUserId(null);
          }}
          schoolUserId={editingSchoolUserId}
          onSuccess={() => {
            setSuccessAction("updateSchoolUser");
            setShowSuccessModal(true);
          }}
        />
      )}

      {showSystemAdminCreateForm && (
        <SystemAdminCreateForm
          isOpen={showSystemAdminCreateForm}
          onClose={() => setShowSystemAdminCreateForm(false)}
          onSuccess={() => {
            setShowSystemAdminCreateForm(false);
            setSuccessAction("createSystemAdmin");
            setShowSuccessModal(true);
            fetchSystemAdmins(1, systemAdminSearchTerm);
          }}
        />
      )}

      {showSystemAdminEditForm && (
        <SystemAdminEditForm
          isOpen={showSystemAdminEditForm}
          onClose={() => {
            setShowSystemAdminEditForm(false);
            setEditingSystemAdminId(null);
          }}
          adminId={editingSystemAdminId}
          onSuccess={() => {
            setSuccessAction("updateSystemAdmin");
            setShowSuccessModal(true);
          }}
        />
      )}

      {showParentCreateForm && (
        <ParentCreateForm
          isOpen={showParentCreateForm}
          onClose={() => {
            setShowParentCreateForm(false);
          }}
          onSuccess={() => {
            setSuccessAction("createParent");
            setShowSuccessModal(true);
          }}
        />
      )}

      {showParentEditForm && (
        <ParentEditForm
          isOpen={showParentEditForm}
          onClose={() => {
            setShowParentEditForm(false);
            setEditingParentId(null);
          }}
          parentId={editingParentId}
          onSuccess={() => {
            setSuccessAction("updateParent");
            setShowSuccessModal(true);
          }}
        />
      )}

      {showParentDetailModal && selectedParentId && (
        <ParentDetailModal
          isOpen={showParentDetailModal}
          onClose={() => {
            setShowParentDetailModal(false);
            setSelectedParentId(null);
          }}
          parentId={selectedParentId}
        />
      )}

      {showStudentDetailModal && selectedStudentDetailId && (
        <StudentDetailModal
          isOpen={showStudentDetailModal}
          onClose={() => {
            setShowStudentDetailModal(false);
            setSelectedStudentDetailId(null);
          }}
          studentId={selectedStudentDetailId}
        />
      )}

      {showSuccessModal && renderSuccessModal()}
      {showDeleteModal && renderDeleteModal()}
    </div>
  );
};

export default Dashboard;
